import { useEffect, useState } from 'react'
import { QUIZ_QUERY_PARAMS } from '@mc/find-my-classes'
import { useStaticData } from '@mc/static-data-react'
import { subcategoryAccessor, categoryAccessor } from '@mc/user-data'
import {
  AI_MOTIVATION_QUERY_PARAM,
  useCategoryNameToIdMap,
} from 'pages/find-my-classes/hooks/useCategorySelections'
import type { TSubcategory } from 'pages/find-my-classes/types'

const parseIdsFromUrlParam = (param: string) =>
  param
    ?.split(',')
    .map((n) => Number.parseInt(n, 10))
    .filter((n) => !Number.isNaN(n)) || []

/* How do we know which category or subcategory we want to show?:
  We want to show the one with the highest local data counts (top category/subcategory) for the user.
  There are 3 scenarios of how the user can select a subcategory in the quiz flow:

  opt. 1. User selects one subcategory => return that one
  opt. 2. User selects more than one subcategory => return the top selected
          subcategory (ranked by local subcategory counts when available,
          otherwise the first selected)
  opt. 3. User doesn’t select any subcategory in the quiz
    A. if they have local subcategory data that belongs to one of the 
       selected categories => return the top local subcategory scoped 
       to those categories
    B. if they selected categories but local data doesn't overlap =>
       return the top selected category

  AI motivation has no selected categories so it skips straight
  to the Science & Technology fallback regardless of prior browsing.
*/
export const useTopCategoryId = (): number | undefined => {
  const catNameToId = useCategoryNameToIdMap()
  const { subcategories } = useStaticData<{ subcategories?: TSubcategory[] }>()
  const [params, setParams] = useState<URLSearchParams | null>(null)

  useEffect(() => {
    setParams(new URLSearchParams(window.location.search))
  }, [])

  if (!params) {
    return undefined
  }

  // get selected subcategories in quiz flow
  const subcatIdsParam = params.get(QUIZ_QUERY_PARAMS.SELECTED_SUBCATEGORIES)

  // get local data counts
  const sortedSubcatIds = subcategoryAccessor.getSortedIdsByCount()

  // user selects subcategories in the quiz
  if (subcatIdsParam) {
    const subcatIds = parseIdsFromUrlParam(subcatIdsParam)

    // opt. 1
    if (subcatIds.length === 1) {
      return subcatIds[0]
    }

    // opt. 2
    return sortedSubcatIds.find((id) => subcatIds.includes(id)) || subcatIds[0]
  }

  // get selected categories in quiz flow
  const categoryIdsParam = params.get(QUIZ_QUERY_PARAMS.SELECTED_CATEGORIES)

  if (categoryIdsParam) {
    const catIds = parseIdsFromUrlParam(categoryIdsParam)

    // opt. 3.A: prefer a locally-ranked subcategory that belongs to one of
    // the user's selected categories (so prior Music browsing can't win
    // when the user just picked Outdoor Adventures this session)
    const catIdsSet = new Set(catIds)
    const scopedSubcatId = sortedSubcatIds.find((subcatId) => {
      const subcat = subcategories?.find((sc) => sc.id === subcatId)
      return subcat ? catIdsSet.has(subcat.parentId) : false
    })
    if (scopedSubcatId !== undefined) {
      return scopedSubcatId
    }

    // opt. 3.B: no local subcategory matches — use the top selected category
    const sortedCategoryIds = categoryAccessor.getSortedIdsByCount()
    return sortedCategoryIds.find((id) => catIds.includes(id)) || catIds[0]
  }

  // AI motivation has no real category mapping;
  // fall back to Science & Technology for the personalized image
  if (params.get(AI_MOTIVATION_QUERY_PARAM) === 'true') {
    return catNameToId.get('Science & Technology')
  }

  return undefined
}
